import { Accordion, AccordionDetails, AccordionSummary, Box, MenuItem, Select, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import { ImageLoader } from '../imgLoader/imgLoader';
import HomeIcon from '../../logo/home.png';
import { Address, CustomerAddress, CustomerInfo } from 'orderme-api-integration-client';
import { useCallback, useEffect, useState } from 'react';
import { clearDelivery, deliverToAddress } from '../../redux/reducers/pickupReducer';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import { NewAddress } from '../addressBook/newAddress';
import { DeliveryDetails } from './whereTo';
import { ItemDescription } from './itemDescription';
import { ChooseAddress } from '../addressBook/chooseAddress';

interface AddressItemProps {
  deliveryDetails: DeliveryDetails;
  onClick: () => void;
  isSelected: boolean;
}

export function AddressItem(props: AddressItemProps) {
  const { onClick, isSelected, deliveryDetails } = props;
  const { t } = useTranslation(['address', 'payment']);
  const dispatch = useAppDispatch();
  const [currentAddress, setCurrentAddress] = useState<CustomerAddress>();
  const [clientAddresses, setClientAddresses] = useState<CustomerAddress[]>();
  const [isAddingNewAddress, setAddingNewAddress] = useState<boolean>(false);
  const userInfo: CustomerInfo = useAppSelector((state: RootState) => state.customerState.customerInfo);
  const isUserLoggedIn = useAppSelector((state: RootState) => state.authState.isLoggedIn);
  const totalPrice = useAppSelector((state: RootState) => state.shoppingCartState.totalPrice);
  const newAddressItem = {
    address: t('address:addNewAddress') as string,
  } as CustomerAddress;
  const priceUpToFreeDelivery =
    deliveryDetails.freeDeliveryFrom > 0 ? deliveryDetails.freeDeliveryFrom - totalPrice : 0;
  const isEnabled = priceUpToFreeDelivery <= 0 || deliveryDetails.price > 0;
  const description =
    priceUpToFreeDelivery > 0
      ? [
          t('payment:freeDeliveryFrom', {
            price: deliveryDetails.freeDeliveryFrom.toPrice(),
          }),
          t('payment:priceUpToFreeDelivery', {
            price: priceUpToFreeDelivery.toPrice(),
          }),
        ]
      : [];

  useEffect(() => {
    if (currentAddress) {
      setAddingNewAddress(false);
      dispatch(
        deliverToAddress({
          address: currentAddress.address,
          apartmentNumber: currentAddress.apartmentNumber,
        } as CustomerAddress)
      );
    } else {
      dispatch(clearDelivery());
    }
  }, [currentAddress, dispatch]);

  const setDefaultAddress = useCallback(() => {
    if (clientAddresses?.length > 0) {
      setCurrentAddress((prev) => {
        if (prev && clientAddresses.some((addr) => isSameAddress(prev, addr))) {
          return clientAddresses.find((addr) => isSameAddress(prev, addr)) || clientAddresses[0];
        }
        return clientAddresses[0];
      });
    } else {
      setCurrentAddress(null);
    }
  }, [clientAddresses]);

  const assignAddresses = useCallback(() => {
    if (userInfo?.addresses) {
      let addresses = userInfo?.addresses.map((item) => item.address) || [];
      setClientAddresses(
        addresses.sort((a, b) => {
          if (Number(b.isPrimary) !== Number(a.isPrimary)) {
            return Number(b.isPrimary) - Number(a.isPrimary);
          }
          return a.address.localeCompare(b.address, 'lt');
        })
      );
      setAddingNewAddress(!addresses || addresses.length === 0);
    }
  }, [userInfo]);

  useEffect(() => {
    setDefaultAddress();
  }, [setDefaultAddress]);

  useEffect(() => {
    if (!isSelected) {
      setClientAddresses(null);
    } else {
      assignAddresses();
    }
  }, [assignAddresses, isSelected]);

  const isSameAddress = (address1: CustomerAddress, address2: CustomerAddress): boolean => {
    if (!address1 || !address2) {
      return false;
    }

    if (address1.address !== address2.address) {
      return false;
    }

    if (!address1.apartmentNumber && !address2.apartmentNumber) {
      return true;
    }

    return address1.apartmentNumber === address2.apartmentNumber;
  };

  const getFullAddress = (address: CustomerAddress): string => address.address + (address.apartmentNumber ?? '');

  const updateSelectedAddress = (fullAddress: string) => {
    const selectedAddress = clientAddresses.find((address) => getFullAddress(address) === fullAddress);
    setCurrentAddress(selectedAddress);
    if (!selectedAddress) {
      setAddingNewAddress(true);
    }
  };

  const onAccordionClick = () => {
    if (!isSelected && isEnabled) {
      onClick();
    }
  };

  const addressSelected = (address: Address, apartmentNo: string) => {
    if (address) {
      setCurrentAddress({
        address: address.fullAddress,
        apartmentNumber: apartmentNo,
      } as CustomerAddress);
    } else {
      setCurrentAddress(null);
    }
  };

  const onAddressAdded = (address: CustomerAddress) => {
    if (address) {
      setCurrentAddress(address);
    } else {
      setDefaultAddress();
    }
    setAddingNewAddress(false);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', my: '20px' }}>
      <Accordion expanded={isSelected} sx={{ backgroundColor: !isEnabled && '#D2CDC3', width: '100%' }}>
        <AccordionSummary
          aria-controls="panel-content"
          id="panel-header"
          onClick={onAccordionClick}
          style={{ cursor: 'pointer' }}
        >
          <Box>
            <Box display="flex" alignItems="center" sx={{ width: '100%' }}>
              {isSelected ? (
                <RadioButtonCheckedIcon color="secondary" />
              ) : (
                <RadioButtonUncheckedIcon sx={{ color: 'rgba(0, 0, 0, 0.54);' }} />
              )}
              <Box display="flex" alignItems="center" sx={{ width: '65px', ml: '10px' }}>
                <ImageLoader src={HomeIcon} sx={{ height: '50px' }} alt="SalesPoint" />
              </Box>
              <Typography
                sx={{
                  alignContent: 'center',
                  ml: '10px',
                  color: !isEnabled && 'rgba(0, 0, 0, 0.54);',
                }}
              >
                {t('salesPoint:deliveryFromHome')}
              </Typography>
            </Box>
            <ItemDescription description={description} />
          </Box>
        </AccordionSummary>
        {isEnabled && (
          <AccordionDetails>
            <Box sx={{ px: '20px', ml: '15px', color: 'black' }}>
              {isUserLoggedIn && (
                <>
                  <Select
                    sx={{
                      width: '100%',
                      borderRadius: '15px',
                      mb: '10px',
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          '& .MuiMenuItem-root': {
                            whiteSpace: 'normal',
                          },
                        },
                      },
                    }}
                    labelId="address-select-label"
                    id="address-select"
                    value={currentAddress ? getFullAddress(currentAddress) : newAddressItem.address}
                    onChange={(event) => updateSelectedAddress(event.target.value)}
                  >
                    {clientAddresses?.map((address) => (
                      <MenuItem value={getFullAddress(address)}>
                        {address.apartmentNumber
                          ? address.address +
                            ' ' +
                            t('payment:apartmentNo', {
                              apartmentNo: address.apartmentNumber,
                            })
                          : address.address}
                      </MenuItem>
                    ))}
                    <MenuItem sx={{ fontWeight: 'bold' }} value={newAddressItem.address}>
                      {newAddressItem.address}
                    </MenuItem>
                  </Select>
                  {isAddingNewAddress && (
                    <NewAddress
                      onCancel={setDefaultAddress}
                      onAdded={(address) => {
                        onAddressAdded(address);
                      }}
                    />
                  )}
                </>
              )}
              {!isUserLoggedIn && (
                <ChooseAddress
                  onAddressSelected={(address, apartment) => {
                    addressSelected(address, apartment);
                  }}
                />
              )}
            </Box>
          </AccordionDetails>
        )}
      </Accordion>
    </Box>
  );
}
